import { useReducer } from 'react';

import { ListViewItem } from '@ingka/list-view';
import Modal, { ModalBody, ModalHeader, Sheets } from '@ingka/modal';
import { translate } from '@repo/phrase';
import { assetStatusTypes } from '@repo/types/asset';
import { Heading, Paragraph } from '@repo/ui/typography';
import { formatDateWithWeekday } from '@repo/utils';
import { invariant } from '@repo/utils/type.utils';

import { useAsset, useAssetParams } from '@/hooks/asset.hooks';
import { getAppConfig } from '@/utils/window.utils';

import styles from './styles.module.scss';

const calculateDaysUntilExpiry = (expiryDate: string, quotationStatus: string | undefined) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    today.setHours(0, 0, 0, 0);
    expiry.setHours(0, 0, 0, 0);
    const timeDiff = expiry.getTime() - today.getTime();
    const diffDays = Math.ceil(timeDiff / (1000 * 3600 * 24));

    if (quotationStatus === assetStatusTypes.EXPIRED || diffDays < 0) {
        return translate('quotation_settings_expired');
    }
    if (diffDays === 0) {
        return translate('quotation_settings_today');
    }
    return translate('quotation_settings_expiry_date', { days: diffDays.toString() });
};

export const QuotationSettings = () => {
    const { assetId, assetType } = useAssetParams();
    invariant(assetType === 'quote');
    const { data } = useAsset(assetType, assetId);
    const expiryDateString = data?.assetDetails.validTill;
    const quotationStatus = data?.assetDetails.status;
    const followUpDateString = data?.assetDetails.metaData?.followUpDateWithCustomer;

    const [visible, toggleVisible] = useReducer((prev) => !prev, false);

    return (
        <>
            <ListViewItem
                title={translate('quotation_settings_title')}
                description={
                    expiryDateString
                        ? calculateDaysUntilExpiry(expiryDateString, quotationStatus)
                        : translate('quotation-settings-model-title')
                }
                emphasised
                control="navigational"
                controlIcon="chevronRight"
                data-testid="toggle-quotation-settings-btn"
                onClick={toggleVisible}
            />
            <Modal handleCloseBtn={toggleVisible} visible={visible} data-testid="quotation-settings-modal">
                <Sheets
                    aria-label={translate('quotation-settings-model-title')}
                    header={<ModalHeader title={translate('quotation-settings-model-title')} />}
                    footer={null}
                >
                    <ModalBody>
                        <div className={styles.quotation_settings} data-testid="quotations-settings-expiry-date">
                            {expiryDateString && (
                                <div className={styles.quotations_settings_expiry_date_field}>
                                    <Heading headingSize="xs">{translate('quotation-settings-expiry-date')}</Heading>
                                    <Paragraph>
                                        {`${calculateDaysUntilExpiry(expiryDateString, quotationStatus)} (${formatDateWithWeekday(expiryDateString, getAppConfig().dateFormat)})`}
                                    </Paragraph>
                                </div>
                            )}
                            {followUpDateString && (
                                <div>
                                    <Heading headingSize="xs">{translate('quotation-settings-followup-date')}</Heading>
                                    <Paragraph>
                                        {formatDateWithWeekday(followUpDateString, getAppConfig().dateFormat)}
                                    </Paragraph>
                                </div>
                            )}
                        </div>
                    </ModalBody>
                </Sheets>
            </Modal>
        </>
    );
};
